'use client';
import { alpha, Stack, useTheme, lighten, Box } from '@mui/material';
import Image from 'next/image';
import useIsMobile from '../../../utils/useIsMobile';
import { useSwipeable } from 'react-swipeable';
import { useEffect, useMemo, useState } from 'react';

type Props = {
  images: {
    url: string;
    thumbnailUrl: string;
    blurUrl?: string;
    alt?: string;
  }[];
  selected: number;
  onClick: (newSelected: number) => void;
  initIsMobile?: boolean;
};

const MAX_VISIBLE = 4;

const Thumbnails = ({ images, selected, onClick, initIsMobile }: Props) => {
  const isMobile = useIsMobile(initIsMobile);
  const LENGTH = images.length;
  const WIDTH_MODIFIER = isMobile ? 24 : 0;
  const theme = useTheme();
  const [moving, setMoving] = useState(0);
  const [firstVisible, setFirstVisible] = useState(0);
  const lastVisible = useMemo(() => firstVisible + MAX_VISIBLE, [firstVisible]);

  const handlers = useSwipeable({
    onSwiped: (event) => {
      event.event.stopPropagation();
    },
    onSwiping: (event) => {
      if (event.dir === 'Left' || event.dir === 'Right') {
        const reference =
          event.dir === 'Right' ? firstVisible : LENGTH - lastVisible;
        const absMax = reference * (80 - WIDTH_MODIFIER) + 20;
        const delta = Math.min(absMax, Math.abs(event.deltaX));
        setMoving(delta * Math.sign(event.deltaX));
      }
    },
  });

  const moveVisibility = (step: number) => {
    if (MAX_VISIBLE >= LENGTH) return setFirstVisible(0);

    let newVisible = firstVisible + step;
    if (newVisible + MAX_VISIBLE > LENGTH) {
      newVisible = LENGTH - MAX_VISIBLE;
    }
    if (newVisible < 0) {
      newVisible = 0;
    }
    setFirstVisible(newVisible);
  };

  useEffect(() => {
    setMoving(0);
  }, [selected]);

  useEffect(() => {
    if (selected >= lastVisible - 1) {
      moveVisibility(MAX_VISIBLE);
    }
    if (selected <= firstVisible) {
      moveVisibility(-MAX_VISIBLE);
    }
  }, [firstVisible, selected, lastVisible]);

  if (LENGTH < 2) return null;
  return (
    <div {...handlers}>
      <Box
        sx={{
          mb: 3,
          width: '100%',
          position: 'absolute',
          bottom: 0,
          left: 0,
          zIndex: 10,
          height: `calc(100px - ${WIDTH_MODIFIER}px)`,
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `calc(340px - ${WIDTH_MODIFIER * MAX_VISIBLE}px)`,
            overflow: 'hidden',
            m: 'auto',
          }}
        >
          <Stack
            direction={'row'}
            spacing={0.5}
            sx={{
              height: '100%',
              alignItems: 'center',
              justifyContent: 'start',
              transition: theme.transitions.create('all'),
              transform: `translateX(${-firstVisible * (80 - WIDTH_MODIFIER) + moving}px)`,
              borderRadius: 0.4,
            }}
          >
            {images.map((image, index) => {
              const alt = `${image.alt || 'Image ' + index} thumbnail`;
              const isSelected = index === selected;
              const size = 100 - (isSelected ? 0 : 24) - WIDTH_MODIFIER;
              return (
                <Image
                  key={image.thumbnailUrl + index}
                  src={image.thumbnailUrl}
                  alt={alt}
                  width={size}
                  height={size}
                  onClick={() => onClick(index)}
                  placeholder={image.blurUrl ? 'blur' : 'empty'}
                  blurDataURL={image.blurUrl}
                  style={{
                    aspectRatio: 1,
                    objectFit: 'cover',
                    borderRadius: 'inherit',
                    borderWidth: '2px',
                    opacity: isSelected ? 1 : 0.8,
                    borderColor: isSelected
                      ? lighten(theme.palette.secondary.light, 0.4)
                      : alpha(theme.palette.secondary.light, 0.4),
                    borderStyle: 'solid',
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: theme.transitions.create('all'),
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      </Box>
    </div>
  );
};

export default Thumbnails;
